/**
 * Firestore Database Schema
 * This file defines the structure and validation for all collections
 */

// Collection names
export const COLLECTIONS = {
  USERS: 'users',
  CONTACTS: 'contacts',
  ANALYTICS: 'analytics',
  FEEDBACK: 'feedback',
  SUBSCRIPTIONS: 'subscriptions',
  MIGRAINE_LOGS: 'migraine_logs',
  PREDICTIONS: 'predictions'
};

// Schema definitions with validation
export const SCHEMAS = {
  // User profile schema
  [COLLECTIONS.USERS]: {
    fields: {
      uid: { type: 'string', required: true },
      email: { type: 'string', required: true, format: 'email' },
      displayName: { type: 'string', required: false },
      firstName: { type: 'string', required: false },
      lastName: { type: 'string', required: false },
      phone: { type: 'string', required: false },
      dateOfBirth: { type: 'timestamp', required: false },
      gender: { type: 'string', required: false, enum: ['male', 'female', 'other', 'prefer_not_to_say'] },
      country: { type: 'string', required: false },
      timezone: { type: 'string', required: false },
      subscriptionStatus: { type: 'string', required: false, enum: ['free', 'trial', 'active', 'cancelled', 'expired'] },
      subscriptionPlan: { type: 'string', required: false, enum: ['basic', 'premium', 'enterprise'] },
      trialStartDate: { type: 'timestamp', required: false },
      trialEndDate: { type: 'timestamp', required: false },
      preferences: {
        type: 'object',
        required: false,
        properties: {
          notifications: { type: 'boolean', default: true },
          emailUpdates: { type: 'boolean', default: true },
          reminderFrequency: { type: 'string', enum: ['daily', 'weekly', 'monthly'] },
          language: { type: 'string', default: 'en' }
        }
      },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true },
      lastLoginAt: { type: 'timestamp', required: false }
    },
    indexes: [
      { fields: ['email'], unique: true },
      { fields: ['subscriptionStatus'] },
      { fields: ['createdAt', 'desc'] }
    ]
  },

  // Contact form submissions
  [COLLECTIONS.CONTACTS]: {
    fields: {
      name: { type: 'string', required: true, minLength: 2, maxLength: 100 },
      email: { type: 'string', required: true, format: 'email' },
      phone: { type: 'string', required: false, maxLength: 20 },
      subject: { type: 'string', required: false, maxLength: 200 },
      message: { type: 'string', required: true, minLength: 10, maxLength: 2000 },
      source: { type: 'string', required: false, default: 'landing_page' },
      category: { type: 'string', required: false, enum: ['general', 'support', 'sales', 'feedback', 'bug_report'] },
      priority: { type: 'string', required: false, enum: ['low', 'medium', 'high', 'urgent'], default: 'medium' },
      status: { type: 'string', required: false, enum: ['new', 'in_progress', 'resolved', 'closed'], default: 'new' },
      assignedTo: { type: 'string', required: false },
      response: { type: 'string', required: false },
      responseDate: { type: 'timestamp', required: false },
      userAgent: { type: 'string', required: false },
      ipAddress: { type: 'string', required: false },
      referrer: { type: 'string', required: false },
      utmSource: { type: 'string', required: false },
      utmMedium: { type: 'string', required: false },
      utmCampaign: { type: 'string', required: false },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true }
    },
    indexes: [
      { fields: ['status'] },
      { fields: ['category'] },
      { fields: ['priority'] },
      { fields: ['createdAt', 'desc'] },
      { fields: ['email'] }
    ]
  },

  // Analytics and tracking data
  [COLLECTIONS.ANALYTICS]: {
    fields: {
      type: { type: 'string', required: true, enum: ['page_view', 'interaction', 'form_submission', 'conversion', 'error'] },
      action: { type: 'string', required: false },
      element: { type: 'string', required: false },
      page: { type: 'string', required: false },
      section: { type: 'string', required: false },
      userId: { type: 'string', required: false },
      sessionId: { type: 'string', required: false },
      userAgent: { type: 'string', required: false },
      ipAddress: { type: 'string', required: false },
      referrer: { type: 'string', required: false },
      utmSource: { type: 'string', required: false },
      utmMedium: { type: 'string', required: false },
      utmCampaign: { type: 'string', required: false },
      utmTerm: { type: 'string', required: false },
      utmContent: { type: 'string', required: false },
      deviceType: { type: 'string', required: false, enum: ['desktop', 'mobile', 'tablet'] },
      browser: { type: 'string', required: false },
      os: { type: 'string', required: false },
      country: { type: 'string', required: false },
      city: { type: 'string', required: false },
      additionalData: { type: 'object', required: false },
      timestamp: { type: 'timestamp', required: true },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: false }
    },
    indexes: [
      { fields: ['type'] },
      { fields: ['page'] },
      { fields: ['userId'] },
      { fields: ['sessionId'] },
      { fields: ['timestamp', 'desc'] },
      { fields: ['type', 'timestamp', 'desc'] }
    ]
  },

  // User feedback and ratings
  [COLLECTIONS.FEEDBACK]: {
    fields: {
      userId: { type: 'string', required: false },
      email: { type: 'string', required: false, format: 'email' },
      rating: { type: 'number', required: true, min: 1, max: 5 },
      category: { type: 'string', required: false, enum: ['general', 'app_usage', 'prediction_accuracy', 'ui_ux', 'support', 'pricing'] },
      comment: { type: 'string', required: false, maxLength: 1000 },
      feature: { type: 'string', required: false },
      version: { type: 'string', required: false },
      deviceInfo: { type: 'object', required: false },
      isPublic: { type: 'boolean', required: false, default: false },
      isAnonymous: { type: 'boolean', required: false, default: false },
      status: { type: 'string', required: false, enum: ['new', 'reviewed', 'addressed'], default: 'new' },
      response: { type: 'string', required: false },
      responseDate: { type: 'timestamp', required: false },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true }
    },
    indexes: [
      { fields: ['rating'] },
      { fields: ['category'] },
      { fields: ['status'] },
      { fields: ['createdAt', 'desc'] },
      { fields: ['userId'] }
    ]
  },

  // Subscription management
  [COLLECTIONS.SUBSCRIPTIONS]: {
    fields: {
      userId: { type: 'string', required: true },
      plan: { type: 'string', required: true, enum: ['free', 'basic', 'premium', 'enterprise'] },
      status: { type: 'string', required: true, enum: ['active', 'cancelled', 'expired', 'past_due', 'trialing'] },
      startDate: { type: 'timestamp', required: true },
      endDate: { type: 'timestamp', required: false },
      trialStartDate: { type: 'timestamp', required: false },
      trialEndDate: { type: 'timestamp', required: false },
      price: { type: 'number', required: false },
      currency: { type: 'string', required: false, default: 'USD' },
      billingCycle: { type: 'string', required: false, enum: ['monthly', 'yearly'] },
      paymentMethod: { type: 'string', required: false },
      stripeCustomerId: { type: 'string', required: false },
      stripeSubscriptionId: { type: 'string', required: false },
      features: { type: 'array', required: false },
      limits: { type: 'object', required: false },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true }
    },
    indexes: [
      { fields: ['userId'] },
      { fields: ['status'] },
      { fields: ['plan'] },
      { fields: ['endDate'] }
    ]
  },

  // Migraine log entries
  [COLLECTIONS.MIGRAINE_LOGS]: {
    fields: {
      userId: { type: 'string', required: true },
      date: { type: 'timestamp', required: true },
      severity: { type: 'number', required: true, min: 1, max: 10 },
      duration: { 
        type: 'string', 
        required: false, 
        enum: ['<1hour', '1-4hours', '4-24hours', '>24hours', 'ongoing'] 
      },
      location: { type: 'array', required: false },
      symptoms: { type: 'array', required: false },
      triggers: { type: 'array', required: false },
      medications: { type: 'array', required: false },
      notes: { type: 'string', required: false, maxLength: 1000 },
      weather: { type: 'object', required: false },
      sleep: { type: 'object', required: false },
      stress: { type: 'number', required: false, min: 1, max: 10 },
      mood: { type: 'number', required: false, min: 1, max: 10 },
      activities: { type: 'array', required: false },
      isPredicted: { type: 'boolean', required: false, default: false },
      predictionAccuracy: { type: 'number', required: false, min: 0, max: 1 },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true }
    },
    indexes: [
      { fields: ['userId', 'date', 'desc'] },
      { fields: ['userId', 'severity'] },
      { fields: ['date', 'desc'] },
      { fields: ['isPredicted'] }
    ]
  },

  // AI predictions
  [COLLECTIONS.PREDICTIONS]: {
    fields: {
      userId: { type: 'string', required: true },
      predictionDate: { type: 'timestamp', required: true },
      predictedDate: { type: 'timestamp', required: true },
      confidence: { type: 'number', required: true, min: 0, max: 1 },
      severity: { type: 'number', required: false, min: 1, max: 10 },
      triggers: { type: 'array', required: false },
      recommendations: { type: 'array', required: false },
      modelVersion: { type: 'string', required: false },
      inputData: { type: 'object', required: false },
      isAccurate: { type: 'boolean', required: false },
      actualOutcome: { type: 'object', required: false },
      feedback: { type: 'string', required: false },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true }
    },
    indexes: [
      { fields: ['userId', 'predictionDate', 'desc'] },
      { fields: ['predictedDate'] },
      { fields: ['confidence'] },
      { fields: ['isAccurate'] }
    ]
  }
};

// Validation functions
export const validateDocument = (collectionName, data) => {
  const schema = SCHEMAS[collectionName];
  if (!schema) {
    throw new Error(`Unknown collection: ${collectionName}`);
  }

  const errors = [];
  const { fields } = schema;

  // Check required fields
  Object.entries(fields).forEach(([fieldName, fieldConfig]) => {
    if (fieldConfig.required && (data[fieldName] === undefined || data[fieldName] === null)) {
      errors.push(`${fieldName} is required`);
    }
  });

  // Validate field types and constraints
  Object.entries(data).forEach(([fieldName, value]) => {
    const fieldConfig = fields[fieldName];
    if (!fieldConfig) {
      errors.push(`Unknown field: ${fieldName}`);
      return;
    }

    // Type validation
    if (value !== null && value !== undefined) {
      switch (fieldConfig.type) {
        case 'string':
          if (typeof value !== 'string') {
            errors.push(`${fieldName} must be a string`);
          } else {
            if (fieldConfig.minLength && value.length < fieldConfig.minLength) {
              errors.push(`${fieldName} must be at least ${fieldConfig.minLength} characters`);
            }
            if (fieldConfig.maxLength && value.length > fieldConfig.maxLength) {
              errors.push(`${fieldName} must be no more than ${fieldConfig.maxLength} characters`);
            }
            if (fieldConfig.enum && !fieldConfig.enum.includes(value)) {
              errors.push(`${fieldName} must be one of: ${fieldConfig.enum.join(', ')}`);
            }
            if (fieldConfig.format === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              errors.push(`${fieldName} must be a valid email address`);
            }
          }
          break;
        case 'number':
          if (typeof value !== 'number') {
            errors.push(`${fieldName} must be a number`);
          } else {
            if (fieldConfig.min !== undefined && value < fieldConfig.min) {
              errors.push(`${fieldName} must be at least ${fieldConfig.min}`);
            }
            if (fieldConfig.max !== undefined && value > fieldConfig.max) {
              errors.push(`${fieldName} must be no more than ${fieldConfig.max}`);
            }
          }
          break;
        case 'boolean':
          if (typeof value !== 'boolean') {
            errors.push(`${fieldName} must be a boolean`);
          }
          break;
        case 'array':
          if (!Array.isArray(value)) {
            errors.push(`${fieldName} must be an array`);
          }
          break;
        case 'object':
          if (typeof value !== 'object' || Array.isArray(value)) {
            errors.push(`${fieldName} must be an object`);
          }
          break;
        case 'timestamp':
          if (!(value instanceof Date) && typeof value !== 'object' && value.seconds === undefined) {
            errors.push(`${fieldName} must be a timestamp`);
          }
          break;
      }
    }
  });

  if (errors.length > 0) {
    throw new Error(`Validation errors: ${errors.join(', ')}`);
  }

  return true;
};

// Helper function to get default values
export const getDefaultValues = (collectionName) => {
  const schema = SCHEMAS[collectionName];
  if (!schema) return {};

  const defaults = {};
  Object.entries(schema.fields).forEach(([fieldName, fieldConfig]) => {
    if (fieldConfig.default !== undefined) {
      defaults[fieldName] = fieldConfig.default;
    }
  });

  return defaults;
};

// Export schema information for documentation
export const getSchemaInfo = () => {
  return Object.entries(SCHEMAS).map(([collectionName, schema]) => ({
    collection: collectionName,
    fields: Object.keys(schema.fields),
    requiredFields: Object.entries(schema.fields)
      .filter(([, config]) => config.required)
      .map(([fieldName]) => fieldName),
    indexes: schema.indexes
  }));
};
